import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../hooks/useLanguage';
import { content } from '../data/content';
import { Section } from './ui/Section';
import { Icon } from './ui/Icon';

const ICONS = {
  consulting:     'compass',
  windowsServer:  'server',
  webDevelopment: 'code',
  windowsOS:      'monitor',
};

const ACCENT = {
  consulting:     'var(--brand)',
  windowsServer:  'var(--mint)',
  webDevelopment: 'var(--accent)',
  windowsOS:      'var(--coral)',
};

const KEYS = ['consulting', 'windowsServer', 'webDevelopment', 'windowsOS'];

const ServiceCard = ({ serviceKey, data, language, index }) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); io.disconnect(); } },
      { threshold: 0.1 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const accent = ACCENT[serviceKey];

  return (
    <motion.div
      ref={ref}
      id={`service-${serviceKey}`}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: 0.05 }}
      className="grid lg:grid-cols-12 gap-6"
    >
      {/* Detail card */}
      <div
        className="lg:col-span-7 rounded-3xl border bg-surface p-5 sm:p-8 lg:p-10 relative overflow-hidden"
        style={{ borderColor: 'var(--border)' }}
      >
        <div
          className="absolute -top-24 -right-24 h-64 w-64 rounded-full blur-3xl opacity-30"
          style={{ background: accent }}
        />
        <div className="relative">
          <div
            className="inline-flex items-center justify-center h-12 w-12 rounded-2xl text-white mb-5"
            style={{ background: accent }}
          >
            <Icon name={ICONS[serviceKey]} size={22} />
          </div>
          <h3 className="text-display text-2xl sm:text-3xl font-bold text-app">{data.title}</h3>
          <p className="mt-2 text-muted">{data.short}</p>
          <p className="mt-5 text-app leading-relaxed">{data.description}</p>

          <ul className="mt-6 space-y-3">
            {data.features.map((f, i) => (
              <motion.li
                key={f}
                initial={{ opacity: 0, x: -8 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.3, delay: 0.15 + 0.06 * i }}
                className="flex items-start gap-3 text-sm"
              >
                <span
                  className="mt-0.5 inline-flex items-center justify-center h-5 w-5 rounded-full text-white shrink-0"
                  style={{ background: accent }}
                >
                  <Icon name="check" size={12} />
                </span>
                <span className="text-muted leading-relaxed">{f}</span>
              </motion.li>
            ))}
          </ul>
        </div>
      </div>

      {/* Outcomes side */}
      <div className="lg:col-span-5 grid gap-4 content-start">
        <div className="rounded-3xl border p-6 bg-elev" style={{ borderColor: 'var(--border)' }}>
          <p className="eyebrow mb-4">{language === 'en' ? 'Outcomes you can plan around' : 'Planbare Ergebnisse'}</p>
          <div className="grid gap-3">
            {data.outcomes.map((o, idx) => (
              <motion.div
                key={o}
                initial={{ opacity: 0, y: 8 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.3, delay: 0.2 + 0.06 * idx }}
                className="flex items-center justify-between rounded-2xl border bg-surface px-4 py-3"
                style={{ borderColor: 'var(--border)' }}
              >
                <span className="text-sm font-medium text-app">{o}</span>
                <span
                  className="text-mono text-[0.65rem] uppercase tracking-[0.18em]"
                  style={{ color: accent }}
                >
                  Target
                </span>
              </motion.div>
            ))}
          </div>
        </div>

        <div
          className="rounded-3xl border p-6 bg-surface flex items-center justify-between"
          style={{ borderColor: 'var(--border)' }}
        >
          <div>
            <p className="text-sm font-semibold text-app">
              {language === 'en' ? 'Need this delivered?' : 'Bereit für den nächsten Schritt?'}
            </p>
            <p className="text-xs text-muted mt-1">
              {language === 'en' ? 'Talk to a senior consultant.' : 'Gemeinsam finden wir die passende Lösung.'}
            </p>
          </div>
          <button
            type="button"
            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-app group"
          >
            {language === 'en' ? 'Start a brief' : 'Beratung anfragen'}
            <Icon name="arrowUpRight" size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export const Services = () => {
  const { language } = useLanguage();
  const t = content[language];
  const [active, setActive] = useState(KEYS[0]);

  // Scrollspy — highlight tab as user scrolls
  useEffect(() => {
    const observers = [];
    KEYS.forEach((key) => {
      const el = document.getElementById(`service-${key}`);
      if (!el) return;
      const io = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) setActive(key); },
        { threshold: 0.4 }
      );
      io.observe(el);
      observers.push(io);
    });
    return () => observers.forEach((o) => o.disconnect());
  }, []);

  const scrollTo = (key) => {
    setActive(key);
    const el = document.getElementById(`service-${key}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Section
      id="services"
      eyebrow={t.services.eyebrow}
      title={t.services.title}
      subtitle={t.services.subtitle}
      align="center"
      className="bg-app"
    >
      {/* Sticky tab bar */}
      <div className="sticky top-[72px] z-40 -mx-5 sm:-mx-6 lg:-mx-8 px-5 sm:px-6 lg:px-8 py-3 bg-app/90 backdrop-blur-md border-b mb-10 sm:mb-14"
           style={{ borderColor: 'var(--border)' }}>
        <div className="flex flex-wrap justify-center gap-2">
          {KEYS.map((key) => {
            const isActive = key === active;
            return (
              <button
                key={key}
                type="button"
                onClick={() => scrollTo(key)}
                className={`relative px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                  isActive ? 'text-white shadow-sm' : 'text-app hover:bg-elev'
                }`}
                style={{
                  border: '1px solid var(--border-strong)',
                  background: isActive ? ACCENT[key] : 'transparent',
                }}
              >
                <span className="flex items-center gap-2">
                  <Icon name={ICONS[key]} size={14} />
                  <span className="hidden xs:inline sm:inline">{t.services[key].title}</span>
                  <span className="sm:hidden">{t.services[key].title.split(' ')[0]}</span>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* All services stacked */}
      <div className="flex flex-col gap-16 sm:gap-20">
        {KEYS.map((key, index) => (
          <ServiceCard
            key={key}
            serviceKey={key}
            data={t.services[key]}
            language={language}
            index={index}
          />
        ))}
      </div>
    </Section>
  );
};

export default Services;
